/**
 * Shared view used by /trading (overview), /trading/dam, /trading/idm.
 * marketFilter: null = both, 'DAM' or 'IDM' = single market focus.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import { useTheme } from '@/hooks/useTheme';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, ComposedChart,
} from 'recharts';

type Market = 'DAM' | 'IDM';

interface SummaryRow {
  market: Market;
  trade_date: string;
  price: string | number | null;
  volume: string | number | null;
  prev_price: string | number | null;
  avg7d: string | number | null;
  day_diff_pct: string | number | null;
}
interface DailyRow { trade_date: string; market: Market; price: number; volume: number; declared_sell: number | null; declared_buy: number | null; }
interface HourlyRow { hour: number; market: Market; price: number; volume: number; }
interface VsUeexRow { trade_date: string; market: Market; oree_price: number; ueex_price: number | null; diff: number | null; }
interface VsGenRow { trade_date: string; hour: number; mwh: number; dam_price: number | null; estimated_revenue_uah: number | null; }

const fmt = (v: any, d = 0) => (v == null || isNaN(Number(v))) ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d });

const todayStr = () => new Date().toISOString().slice(0, 10);
const dayOffset = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export default function OreeMarketView({ marketFilter = null }: { marketFilter?: Market | null }) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [days, setDays] = useState<7 | 14 | 30 | 60>(30);
  const [hourlyDate, setHourlyDate] = useState<string>(marketFilter === 'IDM' ? dayOffset(-1) : todayStr());
  const [summary, setSummary] = useState<SummaryRow[]>([]);
  const [daily, setDaily] = useState<DailyRow[]>([]);
  const [hourly, setHourly] = useState<HourlyRow[]>([]);
  const [vsUeex, setVsUeex] = useState<VsUeexRow[]>([]);
  const [vsGen, setVsGen] = useState<VsGenRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const mQs = marketFilter ? `&market=${marketFilter}` : '';
    Promise.all([
      api.get<{ markets: SummaryRow[] }>('/trading/oree/summary'),
      api.get<{ rows: DailyRow[] }>(`/trading/oree/daily?days=${days}${mQs}`),
      api.get<{ rows: HourlyRow[] }>(`/trading/oree/hourly?date=${hourlyDate}`),
      api.get<{ rows: VsUeexRow[] }>(`/trading/oree/vs-ueex?days=${days}`),
      api.get<{ rows: VsGenRow[] }>(`/trading/oree/vs-generation?days=7`),
    ]).then(([s, d, h, u, g]) => {
      if (cancelled) return;
      setSummary(s.markets || []);
      setDaily(d.rows || []);
      setHourly(h.rows || []);
      setVsUeex(u.rows || []);
      setVsGen(g.rows || []);
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days, hourlyDate, marketFilter]);

  // Pivot daily by date
  const dailyChart = useMemo(() => {
    const m: Record<string, any> = {};
    daily.forEach(r => {
      const k = String(r.trade_date).slice(0, 10);
      m[k] = m[k] || { date: k };
      if (r.market === 'DAM') { m[k].dam = r.price; m[k].dam_vol = r.volume; }
      if (r.market === 'IDM') { m[k].idm = r.price; m[k].idm_vol = r.volume; }
    });
    return Object.values(m).sort((a: any, b: any) => a.date.localeCompare(b.date));
  }, [daily]);

  const hourlyChart = useMemo(() => {
    const m: Record<number, any> = {};
    hourly.forEach(r => {
      m[r.hour] = m[r.hour] || { hour: `${r.hour}:00` };
      if (!marketFilter || r.market === marketFilter) {
        if (r.market === 'DAM') { m[r.hour].dam = r.price; m[r.hour].dam_vol = r.volume; }
        if (r.market === 'IDM') { m[r.hour].idm = r.price; m[r.hour].idm_vol = r.volume; }
      }
    });
    return Object.values(m).sort((a: any, b: any) => parseInt(a.hour) - parseInt(b.hour));
  }, [hourly, marketFilter]);

  const vsUeexChart = useMemo(() => {
    const m: Record<string, any> = {};
    vsUeex.forEach(r => {
      if (marketFilter && r.market !== marketFilter) return;
      const k = String(r.trade_date).slice(0, 10);
      m[k] = m[k] || { date: k };
      if (r.market === 'DAM') m[k].oree_dam = r.oree_price;
      if (r.market === 'IDM') m[k].oree_idm = r.oree_price;
      m[k].ueex_base = r.ueex_price;
    });
    return Object.values(m).sort((a: any, b: any) => a.date.localeCompare(b.date));
  }, [vsUeex, marketFilter]);

  const vsGenChart = useMemo(() => {
    const m: Record<string, any> = {};
    vsGen.forEach(r => {
      const k = String(r.trade_date).slice(0, 10);
      m[k] = m[k] || { date: k, mwh: 0, rev: 0 };
      m[k].mwh += Number(r.mwh) || 0;
      m[k].rev += Number(r.estimated_revenue_uah) || 0;
    });
    return Object.values(m).sort((a: any, b: any) => a.date.localeCompare(b.date));
  }, [vsGen]);

  const totalRev7d = vsGenChart.reduce((s: number, r: any) => s + r.rev, 0);
  const totalMwh7d = vsGenChart.reduce((s: number, r: any) => s + r.mwh, 0);

  // Theme
  const cardBg = isDark ? '#1A1A1F' : '#FFFFFF';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const chartGrid = isDark ? 'rgba(255,255,255,0.06)' : '#F3F4F6';
  const chartTick = isDark ? '#6B6B70' : '#6B7280';

  const Card: any = ({ title, children, right }: any) => (
    <div className="rounded-xl" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${cardBorder}` }}>
        <h3 className="font-semibold text-sm" style={{ color: textMain }}>{title}</h3>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );

  const visibleSummary = marketFilter ? summary.filter(s => s.market === marketFilter) : summary;

  return (
    <div className="space-y-4">
      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {visibleSummary.map(s => (
          <div key={s.market} className="rounded-xl p-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wide" style={{ color: textMuted }}>
                {s.market === 'DAM' ? t('oree_dam_label') : t('oree_idm_label')}
              </div>
              <span className="text-[10px]" style={{ color: textMuted }}>{String(s.trade_date).slice(0, 10)}</span>
            </div>
            <div className="text-2xl font-semibold mt-1" style={{ color: s.market === 'DAM' ? '#F97316' : '#A855F7' }}>
              {fmt(s.price, 0)}
            </div>
            <div className="text-[11px]" style={{ color: textMuted }}>{t('oree_kpi_price_unit_note')}</div>
            <div className="text-[11px] mt-2" style={{ color: textMuted }}>
              {t('oree_volume_label')}: <span style={{ color: textMain }}>{fmt(s.volume, 0)}</span> МВт·год
            </div>
            <div className="text-[11px]" style={{ color: textMuted }}>
              7d avg: <span style={{ color: textMain }}>{fmt(s.avg7d, 0)}</span>
              {s.day_diff_pct != null && (
                <span className="ml-2" style={{ color: Number(s.day_diff_pct) >= 0 ? '#10B981' : '#EF4444' }}>
                  {Number(s.day_diff_pct) >= 0 ? '▲' : '▼'} {fmt(Math.abs(Number(s.day_diff_pct)), 2)}%
                </span>
              )}
            </div>
          </div>
        ))}
        <div className="rounded-xl p-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
          <div className="text-xs uppercase tracking-wide" style={{ color: textMuted }}>{t('oree_potential_revenue_7d')}</div>
          <div className="text-2xl font-semibold mt-1" style={{ color: '#10B981' }}>{fmt(totalRev7d, 0)}</div>
          <div className="text-[11px]" style={{ color: textMuted }}>грн · {fmt(totalMwh7d, 1)} МВт·год факт.</div>
          <div className="text-[10px] mt-2" style={{ color: textMuted }}>{t('oree_potential_revenue_note')}</div>
        </div>
        <div className="rounded-xl p-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
          <div className="text-xs uppercase tracking-wide" style={{ color: textMuted }}>{t('oree_tr_zone_label')}</div>
          <div className="text-2xl font-semibold mt-1" style={{ color: '#3B82F6' }}>{t('oree_kpi_zone_value')}</div>
          <div className="text-[11px]" style={{ color: textMuted }}>{t('oree_kpi_zone_note')}</div>
        </div>
      </div>

      {/* Period selector + daily chart */}
      <Card title={t('oree_daily_price_title')} right={
        <div className="flex rounded text-[11px] overflow-hidden" style={{ border: `1px solid ${cardBorder}` }}>
          {([7, 14, 30, 60] as const).map(d => (
            <button key={d} type="button" onClick={() => setDays(d)}
              className="px-2 py-1 cursor-pointer" style={{
                background: days === d ? '#F97316' : 'transparent',
                color: days === d ? '#fff' : textMuted,
              }}>{d}д</button>
          ))}
        </div>
      }>
        {loading ? <div className="py-12 text-center text-xs" style={{ color: textMuted }}>…</div> : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={dailyChart} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid stroke={chartGrid} strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: chartTick, fontSize: 10 }} />
              <YAxis tick={{ fill: chartTick, fontSize: 10 }} />
              <Tooltip contentStyle={{ background: cardBg, border: `1px solid ${cardBorder}`, fontSize: 11, color: textMain }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {(!marketFilter || marketFilter === 'DAM') && <Line type="monotone" dataKey="dam" name="РДН weighted" stroke="#F97316" strokeWidth={2} dot={false} />}
              {(!marketFilter || marketFilter === 'IDM') && <Line type="monotone" dataKey="idm" name="ВДР weighted" stroke="#A855F7" strokeWidth={2} dot={false} />}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Hourly profile */}
      <Card title={t('oree_hourly_profile_title')} right={
        <input type="date" value={hourlyDate} max={todayStr()} onChange={e => setHourlyDate(e.target.value)}
          className="text-xs px-2 py-1 rounded" style={{ background: 'transparent', border: `1px solid ${cardBorder}`, color: textMain }} />
      }>
        {hourlyChart.length === 0 && !loading ? (
          <div className="py-16 text-center text-sm" style={{ color: textMuted }}>
            <i className="ri-time-line text-2xl block mb-2" style={{ color: textMuted }}></i>
            {t('oree_hourly_empty')}
          </div>
        ) : (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={hourlyChart} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
            <CartesianGrid stroke={chartGrid} strokeDasharray="3 3" />
            <XAxis dataKey="hour" tick={{ fill: chartTick, fontSize: 10 }} />
            <YAxis yAxisId="left" tick={{ fill: chartTick, fontSize: 10 }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: chartTick, fontSize: 10 }} />
            <Tooltip contentStyle={{ background: cardBg, border: `1px solid ${cardBorder}`, fontSize: 11, color: textMain }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {(!marketFilter || marketFilter === 'DAM') && <Bar yAxisId="right" dataKey="dam_vol" name="РДН обсяг" fill="#F97316" opacity={0.25} />}
            {(!marketFilter || marketFilter === 'DAM') && <Line yAxisId="left" type="monotone" dataKey="dam" name="РДН ціна" stroke="#F97316" strokeWidth={2} dot={false} />}
            {(!marketFilter || marketFilter === 'IDM') && <Line yAxisId="left" type="monotone" dataKey="idm" name="ВДР ціна" stroke="#A855F7" strokeWidth={2} dot={false} />}
          </ComposedChart>
        </ResponsiveContainer>
        )}
      </Card>

      {/* OREE vs UEEX BASE */}
      <Card title={t('oree_vs_ueex_title')}>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={vsUeexChart} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
            <CartesianGrid stroke={chartGrid} strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fill: chartTick, fontSize: 10 }} />
            <YAxis tick={{ fill: chartTick, fontSize: 10 }} />
            <Tooltip contentStyle={{ background: cardBg, border: `1px solid ${cardBorder}`, fontSize: 11, color: textMain }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {(!marketFilter || marketFilter === 'DAM') && <Line type="monotone" dataKey="oree_dam" name="OREE РДН weighted" stroke="#F97316" strokeWidth={2} dot={false} />}
            {(!marketFilter || marketFilter === 'IDM') && <Line type="monotone" dataKey="oree_idm" name="OREE ВДР weighted" stroke="#A855F7" strokeWidth={2} dot={false} />}
            <Line type="monotone" dataKey="ueex_base" name="УЕБ BASE realized" stroke="#0066FF" strokeWidth={2} dot={false} strokeDasharray="4 4" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* OREE vs actual generation */}
      <Card title={t('oree_vs_generation_title')}>
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={vsGenChart} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
            <CartesianGrid stroke={chartGrid} strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fill: chartTick, fontSize: 10 }} />
            <YAxis yAxisId="left" tick={{ fill: chartTick, fontSize: 10 }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: chartTick, fontSize: 10 }} />
            <Tooltip contentStyle={{ background: cardBg, border: `1px solid ${cardBorder}`, fontSize: 11, color: textMain }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="left" dataKey="mwh" name="Факт. вироб., МВт·год" fill="#3B82F6" opacity={0.7} />
            <Line yAxisId="right" type="monotone" dataKey="rev" name="Потенц. виторг, грн (DAM × факт.)" stroke="#10B981" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="text-[10px] mt-2" style={{ color: textMuted }}>{t('oree_vs_generation_note')}</div>
      </Card>
    </div>
  );
}
